import { Alert } from '@creditornot/cb-alert';
import { HelpCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, semanticColors } from '@creditornot/cb-ingredients';
import { Tooltip } from '@creditornot/cb-popover';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { ModalHeader } from '@creditornot/cbt-modal';
import { lighten } from 'polished';
import React, { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';
import { useLocalStorageJSON } from 'common/hooks';
import { useIsMobile, MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

export type CampaignSidebarLayoutProps = {
  children: ReactNode;
  preview: ReactNode;
  tooltip: string;
  banner?: ReactNode;
  infoBannerContent?: {
    title: string;
    text: string;
    campaignId: string;
  };
  isFloatingBannerEnabled?: boolean;
};

const StyledRoot = styled.div`
  position: sticky;
  top: 16px;
  display: flex;
  width: 100%;
  max-width: 1200px;
  gap: 40px;
  padding-block: 24px;
  padding-inline: 24px;
  align-items: start;
  margin: 0 auto;

  @media (width< ${MOBILE_BREAKPOINT}px) {
    padding-block-end: 150px;
  }
`;

const StyledContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 70%;
  min-width: auto;
  @media (width< ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
  @media (width > 1200px) {
    flex: 1 1 700px;
  }
`;

const StyledSidebarRoot = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 16px;
  gap: 16px;
  width: 30%;
  min-width: 300px;
  @media (width > 1200px) {
    flex: 1 1 380px;
  }
`;

const StyledSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
`;

const StyledFloatingBanner = styled.button`
  position: fixed;
  z-index: 1;
  inset-block-end: 0;
  inset-inline-start: 0;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  border: none;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  color: ${cbSemanticCssVars.textPrimary};
  background-color: ${({ theme }) => lighten(-0.035, semanticColors[theme.cbThemeMode].surfaceMain)};
  text-align: start;
  cursor: pointer;
`;

const StyledPreviewHeading = styled.div<{ withPadding?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: ${({ withPadding }) => (withPadding ? '16px 16px 0' : '0')};
`;

const StyledHelpIcon = styled(HelpCircleFilled)`
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt72 })};
  width: 14px;
`;

const StyledAlert = styled(Alert)`
  background: ${cssThemedValue({ light: colors.wolt8, dark: colors.wolt12 })};
  svg {
    fill: ${colors.wolt100};
  }
`;

const PreviewHeading = ({ withPadding = true, tooltip }: { withPadding?: boolean; tooltip: string }) => (
  <StyledPreviewHeading withPadding={withPadding}>
    <Typography.Heading6>
      <FormattedMessage id="campaigns.sidebar-preview.title" />
    </Typography.Heading6>

    <Tooltip content={<FormattedMessage id={tooltip} />}>
      <StyledHelpIcon />
    </Tooltip>
  </StyledPreviewHeading>
);

const CampaignSidebarLayout = ({
  children,
  preview,
  banner,
  infoBannerContent,
  isFloatingBannerEnabled,
  tooltip,
}: CampaignSidebarLayoutProps) => {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [campaignIdsInfoBannerHidden, setCampaignIdsInfoBannerHidden] = useLocalStorageJSON<string[]>('infoBanner', []);

  const isMobile = useIsMobile();

  return (
    <StyledRoot>
      <StyledContentWrapper>{children}</StyledContentWrapper>

      {isMobile ? (
        <>
          {isFloatingBannerEnabled && (
            <StyledFloatingBanner onClick={() => setIsPreviewModalOpen(true)}>
              <Typography.Heading6>
                <FormattedMessage id="campaigns.sidebar-preview.title" />
              </Typography.Heading6>
              {!!banner && banner}
            </StyledFloatingBanner>
          )}

          <WoltModal position="bottom" isOpen={isPreviewModalOpen} onRequestClose={() => setIsPreviewModalOpen(false)}>
            <ModalHeader onCloseClick={() => setIsPreviewModalOpen(false)}>
              <PreviewHeading withPadding={false} tooltip={tooltip} />
            </ModalHeader>
            {preview}
          </WoltModal>
        </>
      ) : (
        <StyledSidebarRoot>
          {infoBannerContent && !campaignIdsInfoBannerHidden.includes(infoBannerContent.campaignId) && (
            <StyledAlert
              variant="info"
              title={infoBannerContent.title}
              onCloseButtonClick={
                infoBannerContent.campaignId !== 'NewCampaign'
                  ? () => setCampaignIdsInfoBannerHidden([...campaignIdsInfoBannerHidden, infoBannerContent.campaignId])
                  : undefined
              }
            >
              <FormattedMessage id={infoBannerContent.text} />
            </StyledAlert>
          )}
          <StyledSidebar>
            <PreviewHeading tooltip={tooltip} />
            {preview}
          </StyledSidebar>
        </StyledSidebarRoot>
      )}
    </StyledRoot>
  );
};

export default CampaignSidebarLayout;
